import {
  type ProductionActualOutput,
  type StockTransformation,
} from "@artisancode/api-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { stockTransformationService } from "@/services/stock-transformation";

import { ProductionCancelDialog } from "./production-cancel-dialog";
import { ProductionDetailDialog } from "./production-detail-dialog";
import { ProductionDialog } from "./production-dialog";
import { ProductionSummaryCards } from "./production-summary-cards";
import { ProductionTableCard } from "./production-table-card";

export function ProductionList() {
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] =
    useState<StockTransformation | null>(null);
  const [cancelTarget, setCancelTarget] = useState<StockTransformation | null>(
    null,
  );

  // Fetch production batches
  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ["stock-transformations", "production"],
    queryFn: () =>
      stockTransformationService.list({
        type: "production",
        per_page: 200,
      }),
  });

  const transformations = useMemo(() => {
    return data?.items || [];
  }, [data]);

  // Complete batch mutation
  const completeMutation = useMutation({
    mutationFn: (args: {
      id: string;
      actualOutputs?: ProductionActualOutput[];
    }) =>
      stockTransformationService.complete(args.id, {
        actualOutputs: args.actualOutputs,
      }),
    onSuccess: (updated) => {
      toast.success(
        `Batch ${updated.code} berhasil diselesaikan! Stok toko telah diperbarui.`,
      );
      queryClient.invalidateQueries({
        queryKey: ["stock-transformations", "production"],
      });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setSelectedDetail(null);
    },
  });

  // Cancel batch mutation
  const cancelMutation = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      stockTransformationService.cancel(args.id, { cancelReason: args.reason }),
    onSuccess: (updated) => {
      toast.success(`Batch ${updated.code} berhasil dibatalkan.`);
      queryClient.invalidateQueries({
        queryKey: ["stock-transformations", "production"],
      });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setCancelTarget(null);
      setSelectedDetail(null);
    },
  });

  // Summary metrics
  const summaryMetrics = useMemo(() => {
    const totalCount = transformations.length;
    const completedCount = transformations.filter(
      (t) => t.status === "completed",
    ).length;
    const draftCount = transformations.filter(
      (t) => t.status === "draft",
    ).length;

    const totalOutputUnits = transformations
      .filter((t) => t.status === "completed")
      .reduce((sum, t) => sum + t.totalOutputQty, 0);

    return {
      totalCount,
      completedCount,
      draftCount,
      totalOutputUnits,
    };
  }, [transformations]);

  const handleOpenDetail = (trans: StockTransformation) => {
    setSelectedDetail(trans);
  };

  const handleCompleteBatch = async (
    id: string,
    actualOutputs?: ProductionActualOutput[],
  ) => {
    await completeMutation.mutateAsync({ id, actualOutputs });
  };

  const handleCancelRequest = (trans: StockTransformation) => {
    setCancelTarget(trans);
  };

  const handleConfirmCancel = async (id: string, reason: string) => {
    await cancelMutation.mutateAsync({ id, reason });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Produksi Barang Jadi"
        description="Pencatatan batch seduh & racik minuman/makanan dari bahan baku sesuai resep resmi toko."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading || isRefetching}
              className="min-h-[40px] sm:min-h-9"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isRefetching ? "animate-spin" : ""}`}
              />
              Segarkan
            </Button>

            <Button
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              className="min-h-[40px] sm:min-h-9"
            >
              <Plus className="mr-2 h-4 w-4" />
              Mulai Batch Baru
            </Button>
          </div>
        }
      />

      {/* Summary Cards */}
      <ProductionSummaryCards
        totalCount={summaryMetrics.totalCount}
        completedCount={summaryMetrics.completedCount}
        draftCount={summaryMetrics.draftCount}
        totalOutputUnits={summaryMetrics.totalOutputUnits}
      />

      {/* Table Card */}
      <ProductionTableCard
        transformations={transformations}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onViewDetail={handleOpenDetail}
        onComplete={(id) => handleCompleteBatch(id)}
        onCancelRequest={handleCancelRequest}
        isCompleting={completeMutation.isPending}
      />

      {/* Create Dialog */}
      <ProductionDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ["stock-transformations", "production"],
          });
          queryClient.invalidateQueries({ queryKey: ["stocks"] });
          queryClient.invalidateQueries({ queryKey: ["products"] });
        }}
      />

      {/* Detail Dialog */}
      <ProductionDetailDialog
        transformation={selectedDetail}
        open={!!selectedDetail}
        onOpenChange={(open) => {
          if (!open) setSelectedDetail(null);
        }}
        onComplete={handleCompleteBatch}
        onCancelRequest={handleCancelRequest}
        isCompleting={completeMutation.isPending}
      />

      {/* Cancel Dialog */}
      <ProductionCancelDialog
        transformation={cancelTarget}
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
        onConfirm={handleConfirmCancel}
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
}
export default ProductionList;
